import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AccentTheme, AthkarFontPreference, CalcMethodKey, Locale, MotionPreference, TextSize, ThemeMode, UserSettings, VisualStyle } from "../../types/app";
import { CALC_METHODS } from "../../lib/api/adhanCalc";
import { STORAGE_KEYS } from "../../lib/constants/storageKeys";
import { readStorage, writeStorage } from "../../lib/storage";

interface SettingsContextValue {
  readonly settings: UserSettings;
  readonly resolvedTheme: "dark" | "light";
  readonly updateSettings: (patch: Partial<UserSettings>) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

const themeModes: readonly ThemeMode[] = ["system", "dark", "light"];
const accents: readonly AccentTheme[] = ["cyan", "emerald", "amber", "rose"];
const styles: readonly VisualStyle[] = ["glass", "classic", "high-contrast"];
const sizes: readonly TextSize[] = ["default", "large"];
const motions: readonly MotionPreference[] = ["normal", "reduced"];
const athkarFonts: readonly AthkarFontPreference[] = ["modern", "naskh", "classic"];

function oneOf<T extends string>(value: string, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function bool(value: string, fallback: boolean): boolean {
  if (["true", "1", "on"].includes(value)) return true;
  if (["false", "0", "off"].includes(value)) return false;
  return fallback;
}

function hijriOffsetFromStorage(value: string): UserSettings["hijriOffset"] {
  const n = parseInt(value, 10);
  if (n === -2 || n === -1 || n === 0 || n === 1 || n === 2) return n;
  return 0;
}

function calcMethodFromStorage(value: string): CalcMethodKey {
  return value in CALC_METHODS ? (value as CalcMethodKey) : "MWL";
}

function prefersLight(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ?? false;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

function detectLightweightDefault(): boolean {
  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
    deviceMemory?: number;
    hardwareConcurrency?: number;
  };
  const connection = nav.connection;
  const slowNetwork = Boolean(connection?.saveData || ["slow-2g", "2g"].includes(connection?.effectiveType ?? ""));
  const lowMemory = typeof nav.deviceMemory === "number" && nav.deviceMemory <= 4;
  const lowCpu = typeof nav.hardwareConcurrency === "number" && nav.hardwareConcurrency <= 4;
  return slowNetwork || lowMemory || lowCpu || prefersReducedMotion();
}

function readInitialSettings(): UserSettings {
  const reduced = prefersReducedMotion();
  const lightweightDefault = detectLightweightDefault();

  return {
    locale: readStorage(STORAGE_KEYS.settings.locale, document.documentElement.lang) === "en" ? "en" : "ar",
    theme: oneOf(readStorage(STORAGE_KEYS.settings.theme, "dark"), themeModes, "dark"),
    accent: oneOf(readStorage(STORAGE_KEYS.settings.accent, "emerald"), accents, "emerald"),
    visualStyle: oneOf(readStorage(STORAGE_KEYS.settings.visualStyle, "glass"), styles, "glass"),
    textSize: oneOf(readStorage(STORAGE_KEYS.settings.textSize, "default"), sizes, "default"),
    motion: oneOf(readStorage(STORAGE_KEYS.settings.motion, reduced ? "reduced" : "normal"), motions, reduced ? "reduced" : "normal"),
    athkarFont: oneOf(readStorage(STORAGE_KEYS.settings.athkarFont, "modern"), athkarFonts, "modern"),
    lightweight: bool(readStorage(STORAGE_KEYS.settings.lightweight, lightweightDefault ? "true" : "false"), lightweightDefault),
    sounds: bool(readStorage(STORAGE_KEYS.settings.sounds, "false"), false),
    notifications: bool(readStorage(STORAGE_KEYS.settings.notifications, "false"), false),
    hijriOffset: hijriOffsetFromStorage(readStorage(STORAGE_KEYS.settings.hijriOffset, "0")),
    prayerSource: readStorage(STORAGE_KEYS.settings.prayerSource, "mara") === "calculated" ? "calculated" : "mara",
    calcMethod: calcMethodFromStorage(readStorage(STORAGE_KEYS.settings.calcMethod, "MWL"))
  };
}

function resolveTheme(theme: ThemeMode): "dark" | "light" {
  if (theme === "system") return prefersLight() ? "light" : "dark";
  return theme;
}

function persist(settings: UserSettings): void {
  writeStorage(STORAGE_KEYS.settings.locale, settings.locale);
  writeStorage(STORAGE_KEYS.settings.theme, settings.theme);
  writeStorage(STORAGE_KEYS.settings.accent, settings.accent);
  writeStorage(STORAGE_KEYS.settings.visualStyle, settings.visualStyle);
  writeStorage(STORAGE_KEYS.settings.textSize, settings.textSize);
  writeStorage(STORAGE_KEYS.settings.motion, settings.motion);
  writeStorage(STORAGE_KEYS.settings.athkarFont, settings.athkarFont);
  writeStorage(STORAGE_KEYS.settings.lightweight, String(settings.lightweight));
  writeStorage(STORAGE_KEYS.settings.sounds, String(settings.sounds));
  writeStorage(STORAGE_KEYS.settings.notifications, String(settings.notifications));
  writeStorage(STORAGE_KEYS.settings.hijriOffset, String(settings.hijriOffset));
  writeStorage(STORAGE_KEYS.settings.prayerSource, settings.prayerSource);
  writeStorage(STORAGE_KEYS.settings.calcMethod, settings.calcMethod);

  writeStorage("dini.lang", settings.locale);
  writeStorage("dini.theme", settings.theme === "system" ? "auto" : settings.theme);
}

function applyToDocument(settings: UserSettings, resolvedTheme: "dark" | "light"): void {
  const root = document.documentElement;
  const motion = settings.motion === "reduced" || prefersReducedMotion() ? "reduced" : "normal";

  root.lang = settings.locale;
  root.dir = settings.locale === "ar" ? "rtl" : "ltr";
  root.dataset.theme = resolvedTheme;
  root.dataset.themePreference = settings.theme;
  root.dataset.accentTheme = settings.accent;
  root.dataset.visualStyle = settings.visualStyle;
  root.dataset.textSize = settings.textSize;
  root.dataset.uiMotion = motion;
  root.dataset.athkarFont = settings.athkarFont;
  root.dataset.lightweight = settings.lightweight ? "true" : "false";
  root.style.colorScheme = resolvedTheme;

  document.body.dir = root.dir;
  document.body.lang = settings.locale;
  document.body.classList.toggle("theme-light", resolvedTheme === "light");
  document.body.classList.toggle("theme-dark", resolvedTheme === "dark");
  document.body.classList.toggle("reduce-motion", motion === "reduced");
  document.body.classList.toggle("lightweight-mode", settings.lightweight);

  const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeMeta) themeMeta.content = resolvedTheme === "light" ? "#edf4ff" : "#071120";
}

export function SettingsProvider({ children }: { readonly children: ReactNode }) {
  const [settings, setSettings] = useState<UserSettings>(readInitialSettings);
  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">(() => resolveTheme(settings.theme));

  useEffect(() => {
    const nextTheme = resolveTheme(settings.theme);
    setResolvedTheme(nextTheme);
    applyToDocument(settings, nextTheme);
    persist(settings);
  }, [settings]);

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: light)");
    if (!media) return undefined;
    const sync = () => setResolvedTheme(resolveTheme(settings.theme));
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [settings.theme]);

  const updateSettings = useCallback((patch: Partial<UserSettings>) => {
    setSettings((current) => ({ ...current, ...patch }));
  }, []);

  const value = useMemo<SettingsContextValue>(() => ({
    settings,
    resolvedTheme,
    updateSettings
  }), [resolvedTheme, settings, updateSettings]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
  const value = useContext(SettingsContext);
  if (!value) throw new Error("useSettings must be used within SettingsProvider");
  return value;
}
